import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import {
  CompanyViewContainer,
  FlexRow,
  SectionContainer,
} from "./AdminView.styled";
import ProductsList from "@modules/Products/ProductsList";
import BankAccountList from "@modules/BankAccount/BankAccountList";
import VatList from "@modules/VAT/VatList/VatList";
import AdminList from "../AdminList/AdminList";
import MentionGroupList from "@modules/MentionGroup/MentionGroupList/MentionGroupList";
import PaymentTermsList from "@modules/PaymentTerms/PaymentTermsList";
import TransportList from "@modules/Transport/TransportList";
import SpecificConditionList from "@modules/SpecificCondition/SpecificConditionList";
import Translations from "@modules/Translations/Translation";
import MailTokenList from "@modules/MailToken/MailTokenList/MailTokenList";
import ClientUsersList from "@modules/ClientUsers/ClientUsersList/ClientUsersList";
import GeneralParametersList from "@modules/GeneralParameters/GeneralParametersList";
import NotFound from "@components/Errors/NotFound";

/**
 * company GeneralInfo,
 * Path - /company/view/{id}
 * @see See [CenterProduct Requirements](https://hesusstore.atlassian.net/wiki/spaces/HS/pages/2944335881/Company+view+page+-+General+layout)
 * @component
 */
const AdminView = (props) => {
  const { t } = props;
  const params = useParams();
  const history = useNavigate();
  const validPaths = [
    "manager",
    "clientUsers",
    "products",
    "vatlists",
    "bankAccounts",
    "mentiongroups",
    "transport",
    "specificConditions",
    "paymentTerms",
    "generalParameters",
    "emails",
    "translation",
  ];

  const location = useLocation();

  const basePath = location.pathname.split("/").slice(0, 2).join("/") + "/";
  const generalParams = useGetGeneralParameters();
  const [countryList, setCountryList] = useState([]);

  useEffect(() => {
    if (Object.keys(generalParams).length > 0) {
      setCountryList(generalParams.countryList);
    }
  }, [generalParams]);

  if (basePath === "/administration/" && !validPaths.includes(params.path)) {
    return <NotFound />;
  }

  return (
    <CompanyViewContainer>
      <FlexRow>
        <SectionContainer>
          {params.path === "manager" &&
            countryList &&
            countryList.length !== 0 && (
              <AdminList
                columnList={[
                  "ID",
                  "ADMIN_NAME",
                  "ADMIN_EMAIL",
                  "COUNTRIES",
                  "ADMIN_IS_TECH",
                  "ADMIN_LAST_LOGIN",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "products" &&
            countryList &&
            countryList.length !== 0 && (
              <ProductsList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "CATEGORY",
                  "SUB_CATEGORY",
                  "NAME",
                  "PRODUCT_CODE",
                  "OPERATIONS",
                  "ACTIONS",
                ]}
                categories={generalParams.categoryList}
                subCategories={generalParams.subCategoryList}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "translation" && <Translations />}
          {params.path === "mentiongroups" &&
            countryList &&
            countryList.length !== 0 && (
              <MentionGroupList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "MENTION_GROUP_NAME",
                  "MENTION_GROUP_DESCRIPTION",
                  "MENTION_GROUP_ADMINS",
                  "MENTION_GROUP_ADMIN_COUNT",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "bankAccounts" &&
            countryList &&
            countryList.length !== 0 && (
              <BankAccountList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "LABEL",
                  "BANK_NAME",
                  "IBAN",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "vatlists" &&
            countryList &&
            countryList.length !== 0 && (
              <VatList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "LABEL",
                  "VAT_CALCULATION_RATE",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "paymentTerms" &&
            countryList &&
            countryList.length !== 0 && (
              <PaymentTermsList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "LABEL",
                  "PAYMENT_TERM_CALCULATION_CODE",
                  "PAYMENT_TERM_CALCULATION_VALUE",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "specificConditions" &&
            countryList &&
            countryList.length !== 0 && (
              <SpecificConditionList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "LABEL",
                  "SPECIFIC_CONDITIONS_TITLE",
                  "SPECIFIC_CONDITIONS_PARAMETERS",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "emails" &&
            countryList &&
            countryList.length !== 0 && (
              <MailTokenList
                columnList={[
                  "ID",
                  "NAME",
                  "EMAIL_TEMPLATES",
                  "DESCRIPTION",
                  "ACTIONS",
                ]}
              />
            )}
          {params.path === "transport" &&
            countryList &&
            countryList.length !== 0 && (
              <TransportList
                columnList={[
                  "ID",
                  "COUNTRY",
                  "TRANSPORT_TYPE_NAME",
                  "TRANSPORT_TYPE_VOLUME",
                  "TRANSPORT_TYPE_AVG_WEIGHT",
                  "TRANSPORT_TYPE_MIN_WEIGHT",
                  "TRANSPORT_TYPE_MAX_WEIGHT",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "generalParameters" &&
            countryList &&
            countryList.length !== 0 && (
              <GeneralParametersList
                columnList={[
                  "CODE",
                  "COUNTRY",
                  "VALUE",
                  "DESCRIPTION",
                  "ACTIONS",
                ]}
                countries={generalParams.countryList}
              />
            )}
          {params.path === "clientUsers" && (
            <ClientUsersList
              columnList={[
                "ID",
                "CONTACT_NAME",
                "CONTACT_EMAIL",
                "COMPANY",
                "STATUS",
                "CONTACT_LAST_LOGIN",
                "CREATED_AT",
                "ACTIONS",
              ]}
            />
          )}
        </SectionContainer>
      </FlexRow>
    </CompanyViewContainer>
  );
};
AdminView.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(AdminView);
